import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useContent } from "../contexts/ContentContext";
import { Menu, LogOut, Shield } from "lucide-react";
import { useState } from "react";

export const BrandLogo = ({ light = false }) => {
  const { t, img } = useContent();
  const logo = img("brand.logo", "/logo-ba224.png");
  const name = t("brand.name", "Booking Artistes 224");
  if (light) {
    // Fond sombre (footer) : logo posé sur une pastille blanche
    return (
      <div className="inline-flex items-center bg-white rounded-xl px-4 py-2.5 shadow-[0_4px_16px_rgba(0,0,0,0.25)]">
        <img src={logo} alt={name} className="h-14 w-auto" data-testid="brand-logo-footer" />
      </div>
    );
  }
  return <img src={logo} alt={name} className="h-14 w-auto" data-testid="brand-logo" />;
};

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useContent();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const linkCls = ({ isActive }) =>
    `text-sm font-semibold transition-colors duration-200 ${isActive ? "text-[hsl(var(--primary))]" : "text-foreground/70 hover:text-[hsl(var(--primary))]"}`;

  const navLinks = (
    <>
      <NavLink data-testid="nav-explore" to="/explore" className={linkCls}>Artistes</NavLink>
      <NavLink data-testid="nav-events" to="/events" className={linkCls}>Événements</NavLink>
      <NavLink data-testid="nav-how" to="/#how" className="text-sm font-semibold text-foreground/70 hover:text-[hsl(var(--primary))] transition-colors duration-200">Comment ça marche</NavLink>
      {user?.role === "artist" && (
        <NavLink data-testid="nav-artist-events" to="/artist/events" className={linkCls}>Mes événements</NavLink>
      )}
      {user?.role === "artist" && (
        <NavLink data-testid="nav-artist-onboard" to="/artist/onboarding" className={linkCls}>Mon profil artiste</NavLink>
      )}
      {user && user.role !== "artist" && (
        <NavLink data-testid="nav-my-tickets" to="/tickets" className={linkCls}>Mes billets</NavLink>
      )}
      {user && (
        <NavLink data-testid="nav-messages" to="/messages" className={linkCls}>Messages</NavLink>
      )}
      {user && (
        <NavLink data-testid="nav-dashboard" to="/dashboard" className={linkCls}>Tableau de bord</NavLink>
      )}
      {user?.role === "admin" && (
        <NavLink data-testid="nav-admin" to="/admin" className="text-sm font-bold inline-flex items-center gap-1 text-[hsl(var(--secondary))] hover:underline">
          <Shield className="w-3.5 h-3.5" /> Admin
        </NavLink>
      )}
    </>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-border shadow-[0_1px_3px_rgba(13,27,42,0.05)]">
      <div className="container-mc flex items-center justify-between h-[4.5rem]">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group shrink-0">
          <BrandLogo />
        </Link>

        <nav className="hidden md:flex items-center gap-6 flex-wrap">
          {navLinks}
        </nav>

        <div className="hidden md:flex items-center gap-3 shrink-0">
          {user ? (
            <>
              <span className="hidden xl:inline text-xs font-mono text-muted-foreground uppercase tracking-wider whitespace-nowrap max-w-[180px] truncate" data-testid="user-name-display">
                {user.name} · {user.role === "admin" ? "Admin" : user.role === "artist" ? "Artiste" : "Client"}
              </span>
              <button data-testid="logout-btn" onClick={handleLogout} className="btn-ghost">
                <LogOut className="w-4 h-4 mr-1.5" /> Quitter
              </button>
            </>
          ) : (
            <>
              <Link data-testid="header-login-link" to="/login" className="btn-outline text-sm py-2 px-5">Se connecter</Link>
              <Link data-testid="header-register-link" to="/register" className="btn-secondary text-sm py-2 px-5">S'inscrire</Link>
            </>
          )}
        </div>

        <div className="md:hidden">
          {!user && (
            <Link data-testid="mobile-cta" to="/register" className="btn-secondary text-xs py-2 px-4">S'inscrire</Link>
          )}
          {user && (
            <button data-testid="logout-btn-mobile-icon" onClick={handleLogout} className="p-2 -mr-2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Déconnexion">
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>

        <button data-testid="mobile-menu-toggle" className="hidden" onClick={() => setOpen(!open)}>
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-white/95 backdrop-blur-xl">
          <div className="container-mc py-4 flex flex-col gap-4">
            {navLinks}
            {user ? (
              <button data-testid="logout-btn-mobile" onClick={handleLogout} className="btn-outline w-full">Quitter</button>
            ) : (
              <div className="flex gap-2">
                <Link to="/login" className="btn-outline flex-1" onClick={() => setOpen(false)}>Connexion</Link>
                <Link to="/register" className="btn-secondary flex-1" onClick={() => setOpen(false)}>Inscription</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
